import { useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Clock, Info, Tag, Home as HomeIcon, Activity } from 'lucide-react';
import { useRegistry } from '../../registry/hooks';
import { useEntity, useEntityFriendlyName } from '../cards/useEntity';
import { domainForEntityId } from '../cards/EntityCard';
import { domainIcon } from '../cards/icons';

export interface EntityDetailModalProps {
  entityId: string | null;
  onOpenChange: (open: boolean) => void;
}

// Relative Zeit ('vor 3 Min', 'vor 2 Std') aus ISO-Timestamp
function relativeTime(iso?: string): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 30) return 'gerade eben';
  if (diffSec < 90) return 'vor 1 Min';
  if (diffSec < 3600) return `vor ${Math.round(diffSec / 60)} Min`;
  if (diffSec < 7200) return 'vor 1 Std';
  if (diffSec < 86400) return `vor ${Math.round(diffSec / 3600)} Std`;
  const days = Math.round(diffSec / 86400);
  return days === 1 ? 'vor 1 Tag' : `vor ${days} Tagen`;
}

// Formatiert ISO als DE-lokale Uhrzeit
function fmtAbsolute(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const ATTR_SKIP = new Set([
  'friendly_name', 'icon', 'supported_features', 'supported_color_modes', 'device_class',
  'attribution', 'entity_picture',
]);

const ATTR_LABEL: Record<string, string> = {
  brightness: 'Helligkeit (roh)',
  color_temp: 'Farbtemperatur',
  color_temp_kelvin: 'Farbtemperatur (K)',
  min_color_temp_kelvin: 'Min. Farbtemp. (K)',
  max_color_temp_kelvin: 'Max. Farbtemp. (K)',
  min_mireds: 'Min. Mireds',
  max_mireds: 'Max. Mireds',
  color_mode: 'Farbmodus',
  hs_color: 'HS-Farbe',
  rgb_color: 'RGB-Farbe',
  xy_color: 'XY-Farbe',
  temperature: 'Temperatur',
  current_temperature: 'Aktuelle Temperatur',
  target_temp_high: 'Ziel-Temperatur (hoch)',
  target_temp_low: 'Ziel-Temperatur (niedrig)',
  hvac_action: 'HVAC-Aktion',
  hvac_modes: 'HVAC-Modi',
  fan_mode: 'Lueftungs-Modus',
  fan_modes: 'Lueftungs-Modi',
  swing_mode: 'Schwenk-Modus',
  preset_mode: 'Preset',
  preset_modes: 'Presets',
  humidity: 'Luftfeuchte',
  current_humidity: 'Aktuelle Luftfeuchte',
  unit_of_measurement: 'Einheit',
  state_class: 'State-Klasse',
  current_position: 'Aktuelle Position',
  current_tilt_position: 'Aktuelle Kippposition',
  volume_level: 'Lautstaerke',
  media_title: 'Titel',
  media_artist: 'Kuenstler',
  media_album_name: 'Album',
  source: 'Quelle',
  source_list: 'Quellen',
  is_volume_muted: 'Stummgeschaltet',
};

function labelForAttr(key: string): string {
  return ATTR_LABEL[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
  if (Array.isArray(v)) return v.map((x) => formatValue(x)).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function EntityDetailModal({ entityId, onOpenChange }: EntityDetailModalProps): JSX.Element | null {
  const isOpen = entityId !== null;
  const entity = useEntity(entityId ?? undefined);
  const friendly = useEntityFriendlyName(entityId ?? undefined);
  const { state } = useRegistry();

  const areaName = useMemo(() => {
    if (!entity) return null;
    const areaId = entity.area_id ?? (entity.device_id ? state.devices.find((d) => d.device_id === entity.device_id)?.area_id : undefined);
    if (!areaId) return null;
    return state.areas.find((a) => a.area_id === areaId)?.name ?? null;
  }, [entity, state]);

  const attrs = useMemo(() => {
    if (!entity) return [] as Array<[string, unknown]>;
    return Object.entries(entity.attributes ?? {}).filter(([k]) => !ATTR_SKIP.has(k) && !k.startsWith('_'));
  }, [entity]);

  const domain = entityId ? domainForEntityId(entityId) : '';
  const Icon = domain ? domainIcon(domain) : Info;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto sm:max-w-xl">
        <DialogTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-5 w-5 text-[var(--rsc-primary)]" aria-hidden="true" />
          {friendly || entityId || 'Detail'}
        </DialogTitle>
        <DialogDescription className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono dark:bg-gray-700/60">{entityId ?? ''}</span>
          {areaName && (
            <span className="flex items-center gap-1">
              <HomeIcon className="h-3 w-3" aria-hidden="true" />
              {areaName}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Tag className="h-3 w-3" aria-hidden="true" />
            {domain}
          </span>
        </DialogDescription>

        {!entity ? (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Entity nicht gefunden.</p>
        ) : (
          <div className="mt-4 space-y-4">
            <section>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Aktueller Zustand
              </div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold tabular-nums">{formatValue(entity.state)}</div>
                {typeof entity.attributes.unit_of_measurement === 'string' && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">{entity.attributes.unit_of_measurement}</span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Clock className="h-3 w-3" aria-hidden="true" />
                <span>Seit: {relativeTime(entity.last_changed)}</span>
                <span className="opacity-60">({fmtAbsolute(entity.last_changed)})</span>
              </div>
            </section>

            {entity.labels && entity.labels.length > 0 && (
              <section>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Labels
                </div>
                <div className="flex flex-wrap gap-1">
                  {entity.labels.map((l) => (
                    <span key={l} className="rounded-full bg-[var(--rsc-primary)]/10 px-2 py-0.5 text-xs text-[var(--rsc-primary)]">
                      {l}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {attrs.length > 0 && (
              <section>
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <Activity className="h-3 w-3" aria-hidden="true" />
                  Attribute
                </div>
                <dl className="divide-y divide-gray-100 rounded-lg bg-gray-50/60 dark:divide-gray-700/40 dark:bg-gray-800/40">
                  {attrs.map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-4 px-3 py-2">
                      <dt className="text-xs font-medium text-gray-600 dark:text-gray-300">{labelForAttr(k)}</dt>
                      <dd className="max-w-[60%] break-words text-right text-xs text-gray-900 tabular-nums dark:text-white">
                        {formatValue(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
